import { useState, useEffect, useCallback } from 'react'
import { Layers, Plus, Edit2, Trash2, X, Save } from 'lucide-react'
import { apiService } from '../services/api'

const OPERATORS = ['ORANGE', 'PLUS', 'TMOBILE', 'P4']

export default function ProductGroupsManagementPage() {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getAdminProductGroups()
      setGroups(response.data)
    } catch (err) {
      setError('Nie udało się załadować grup produktów')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadGroups() }, [loadGroups])

  const handleDelete = async (group) => {
    try {
      await apiService.deleteProductGroup(group.operator_code, group.group_code)
      setDeleteConfirm(null)
      await loadGroups()
    } catch (err) {
      alert('Błąd: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleSave = async (data) => {
    try {
      if (editingGroup) {
        await apiService.updateProductGroup(
          editingGroup.operator_code,
          editingGroup.group_code,
          data,
        )
      } else {
        await apiService.createProductGroup(data)
      }
      setShowModal(false)
      setEditingGroup(null)
      await loadGroups()
    } catch (err) {
      alert('Błąd: ' + (err.response?.data?.detail || err.message))
    }
  }

  // Group by operator
  const grouped = {}
  OPERATORS.forEach(op => { grouped[op] = groups.filter(g => g.operator_code === op) })

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="w-8 h-8 text-indigo-600" />
            Grupy produktów
          </h1>
          <p className="text-gray-600 mt-1">
            Konfiguracja grup produktów per operator (dane w tabeli product_groups)
          </p>
        </div>
        <button
          onClick={() => { setEditingGroup(null); setShowModal(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus className="w-5 h-5" />
          Dodaj grupę
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {OPERATORS.map(op => {
            const opGroups = grouped[op] || []
            if (opGroups.length === 0) return null
            return (
              <div key={op} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">{op}</h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kod</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                      <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase">Kolejność</th>
                      <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase">Aktywna</th>
                      <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {opGroups.map(g => (
                      <tr key={g.group_code} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-mono text-gray-900">{g.group_code}</td>
                        <td className="px-6 py-3 text-sm text-gray-700">{g.group_name}</td>
                        <td className="px-6 py-3 text-sm text-center text-gray-500">{g.display_order}</td>
                        <td className="px-6 py-3 text-center">
                          {g.active ? (
                            <span className="text-green-600 text-sm">Tak</span>
                          ) : (
                            <span className="text-red-500 text-sm">Nie</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button onClick={() => { setEditingGroup(g); setShowModal(true) }} className="text-blue-600 hover:text-blue-800 mr-3">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteConfirm(g)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <GroupModal
          group={editingGroup}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingGroup(null) }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Usunąć grupę?</h3>
            <p className="text-gray-600 mb-6">
              {deleteConfirm.operator_code} / {deleteConfirm.group_code} — "{deleteConfirm.group_name}"
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Anuluj</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Usuń</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function GroupModal({ group, onSave, onClose }) {
  const isEdit = !!group
  const [form, setForm] = useState({
    operator_code: group?.operator_code || 'ORANGE',
    group_code: group?.group_code || '',
    group_name: group?.group_name || '',
    display_order: group?.display_order ?? 0,
    is_default: group?.is_default ?? false,
    is_aggregate: group?.is_aggregate ?? false,
    active: group?.active ?? true,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      operator_code: form.operator_code,
      group_code: form.group_code.toUpperCase(),
      group_name: form.group_name,
      display_order: parseInt(form.display_order),
      is_default: form.is_default,
      is_aggregate: form.is_aggregate,
      active: form.active,
    }
    if (isEdit) {
      const { operator_code, group_code, ...updatePayload } = payload
      onSave(updatePayload)
    } else {
      onSave(payload)
    }
  }

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edytuj grupę' : 'Nowa grupa'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select value={form.operator_code} onChange={e => set('operator_code', e.target.value)}
                disabled={isEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50">
                {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kod grupy</label>
              <input value={form.group_code} onChange={e => set('group_code', e.target.value)}
                disabled={isEdit} placeholder="np. OPASKA_SOS"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa wyświetlana</label>
            <input value={form.group_name} onChange={e => set('group_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kolejność</label>
              <input type="number" value={form.display_order} onChange={e => set('display_order', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pt-6">
              <input type="checkbox" checked={form.is_aggregate} onChange={e => set('is_aggregate', e.target.checked)}
                className="rounded border-gray-300" />
              Agregat
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 pt-6">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)}
                className="rounded border-gray-300" />
              Aktywna
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Anuluj</button>
            <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              <Save className="w-4 h-4" />
              {isEdit ? 'Zapisz' : 'Utwórz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
